import { ChangeDetectionStrategy, Component, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { SwiperModule } from 'swiper/angular';
import { SwiperOptions } from 'swiper';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { StoryReelStory, StoryTapDirection, StoryViewerComponent } from '@components/story-viewer/story-viewer.component';
import { VideoPlayerModule } from '@components/video-player/video-player.module';
import { ApiClientConstant } from '@cureskin/api-client';
import {
  ADDRESS_ROUTE,
  REGIMEN_FAQ_CTA,
  REGIMEN_FAQ_DOCTOR,
  REGIMEN_FAQ_EVENT,
  RegimenFaqPost,
  STORY_START_INDEX,
} from './regimen-explore-banner.constants';

@Component({
  selector: 'app-regimen-explore-banner',
  templateUrl: './regimen-explore-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styles: [`
    app-regimen-explore-banner .swiper-slide { width: auto !important; }
  `],
  imports: [StoryViewerComponent, VideoPlayerModule, SwiperModule],
})
export class RegimenExploreBannerComponent {
  private readonly conn = inject(ConnectionService);
  private readonly eventLogger = inject(EventLoggerService);
  protected readonly videoType = ApiClientConstant.Feed.Type.Video;

  readonly posts = input<RegimenFaqPost[]>([]);

  /** Index of the post the story reel is opened at; -1 when closed. */
  protected readonly activeIndex = signal(-1);
  protected readonly storyStartIndex = STORY_START_INDEX;
  protected readonly doctorPatch = REGIMEN_FAQ_DOCTOR;

  protected readonly swiperConfig: SwiperOptions = {
    slidesPerView: 'auto',
    spaceBetween: 0,
  };

  protected openStory(post: RegimenFaqPost, index: number): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: REGIMEN_FAQ_EVENT.OPEN, post_id: post.objectId }));
    this.activeIndex.set(index);
  }

  protected onStoryTap(event: { direction: StoryTapDirection; story: StoryReelStory }): void {
    const post = event.story as RegimenFaqPost;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: REGIMEN_FAQ_EVENT.STORY_TAP, post_id: post.objectId, tapValue: event.direction }));
  }

  protected closeStory(): void {
    this.activeIndex.set(-1);
  }

  protected async onCtaClick(post: RegimenFaqPost): Promise<void> {
    if (!post) return;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: REGIMEN_FAQ_EVENT.CTA, CTA_Label: post.ctaText }));
    const url = post.ctaUrl;
    if (!url) return;
    if (url === REGIMEN_FAQ_CTA.ADD_ADDRESS) {
      await this.navigateForAddress();
      return;
    }
    await this.conn.navigateToURL(url);
  }

  /** ADD_ADDRESS sentinel → address entry if no address exists, else checkout. */
  private async navigateForAddress(): Promise<void> {
    const address = await this.conn.fetchDefaultAddress(this.conn.getActingUser()).catch((): null => null);
    const hasAddress = !!address;
    await this.conn.navigateToURL(hasAddress ? ADDRESS_ROUTE.CHECKOUT : ADDRESS_ROUTE.NEW);
  }
}
